import { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { X, Send, Sparkles, ExternalLink, Camera, ImageIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useGeo } from '../contexts/GeoContext';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHANNON_AVATAR = "https://customer-assets.emergentagent.com/job_b74253f6-0045-47f8-b035-a9fa386c3b43/artifacts/awo6inej_Screen%20Shot%202026-04-08%20at%2011.34.45%20PM.png";

// Auto-close the chat this long after Shannon delivers a greeting with no user reply.
const AUTO_CLOSE_MS = 12000;
// How long the small "still here" toast stays visible on subsequent pages
const STILL_HERE_TOAST_MS = 4000;

const buildIntro = (user) => {
  if (user && (user.name || user.email)) {
    const first = (user.name || user.email).split(/[\s@]/)[0];
    const capitalized = first.charAt(0).toUpperCase() + first.slice(1);
    return `Welcome back, ${capitalized}! I'm Shannon — I remember you. Need help finding something today or want a quick style tip? I'm right here.`;
  }
  return "Hey! I'm Shannon, your personal shopping assistant at Doe Wiz. Need help finding the perfect fit, coordinating an outfit, or have any questions? I got you!";
};

const DEPARTMENT_GREETINGS = {
  men: "Welcome to the Men's section! We've got tracksuits, hoodies, tees, and more. Shopping for yourself or someone special? Let me help you find the perfect fit!",
  women: "Welcome to the Women's collection! Whether you're shopping for yourself or picking out a gift, we've got sets, hoodies, and statement pieces. What are you looking for today?",
  boys: "Welcome to the Boys' section! Let me help you find something cool for the young man in your life. We've got matching sets, hoodies, and more!",
  girls: "Welcome to the Girls' collection! We have adorable tracksuits, hoodies, and sets. Shopping for a daughter, niece, or someone special? I've got you!",
  babies: "The Doe Wiz Babies drop is COMING SOON — onesies, tiny caps, and matching sets for baby girls & boys. Want me to add you to the launch list? Just drop your email and I'll ping you the second it's live!",
  hats: "Welcome to Hats & Caps! We've got trucker caps, snapbacks, dad hats, and fitted caps in every color. What's your vibe?",
  accessories: "Welcome to Accessories! Check out our signature Doe Wiz roller perfume oils — great for yourself or as a gift! Need any recommendations?",
};

function getSessionId() {
  let sid = sessionStorage.getItem('shannon_session');
  if (!sid) {
    sid = 'shannon_' + Date.now() + '_' + Math.random().toString(36).slice(2);
    sessionStorage.setItem('shannon_session', sid);
  }
  return sid;
}

export default function ShannonChat() {
  const { formatPrice } = useGeo();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(true);
  const [lastGreetedCategory, setLastGreetedCategory] = useState(null);
  const [pendingImage, setPendingImage] = useState(null); // { dataUrl, base64 }
  const [showStillHere, setShowStillHere] = useState(false); // brief "still here if you need me" toast
  const [userInteracted, setUserInteracted] = useState(false); // once user sends a msg, stop auto-behaviors
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const autoCloseTimerRef = useRef(null);
  const stillHereTimerRef = useRef(null);
  const location = useLocation();

  const cancelAutoClose = () => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  };

  const scheduleAutoClose = (ms = AUTO_CLOSE_MS) => {
    cancelAutoClose();
    autoCloseTimerRef.current = setTimeout(() => {
      // Only auto-close if user hasn't started typing/interacting
      setIsOpen((currentlyOpen) => {
        if (!currentlyOpen) return currentlyOpen;
        // Stay open if user has typed something
        return false;
      });
    }, ms);
  };

  // Seed the initial intro once per browser session, personalised for logged-in users.
  useEffect(() => {
    const alreadyIntroduced = sessionStorage.getItem('shannon_introduced') === '1';
    if (messages.length === 0) {
      if (!alreadyIntroduced) {
        setMessages([{ role: 'assistant', text: buildIntro(user), isIntro: true }]);
      } else {
        // Skip the big intro — she stays a subtle presence
        setMessages([]);
      }
    }
    // Refresh intro copy if user object hydrates after mount and we haven't introduced yet
    if (!alreadyIntroduced && user && messages.length === 1 && messages[0].isIntro) {
      setMessages([{ role: 'assistant', text: buildIntro(user), isIntro: true }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setPulse(false);
      setShowStillHere(false);
      // Schedule auto-close so Shannon doesn't linger
      if (!userInteracted) scheduleAutoClose();
    } else {
      cancelAutoClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Auto-greet when entering a department — one-time-per-session behaviour, gentler on repeat visits.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    if (!category || !DEPARTMENT_GREETINGS[category] || location.pathname !== '/products') return;
    if (category === lastGreetedCategory) return;

    const alreadyIntroduced = sessionStorage.getItem('shannon_introduced') === '1';
    setLastGreetedCategory(category);

    if (!alreadyIntroduced) {
      // FIRST department visit this session → do the full intro-open with the department greeting
      const timer = setTimeout(() => {
        sessionStorage.setItem('shannon_introduced', '1');
        setIsOpen(true);
        setPulse(false);
        setMessages((prev) => {
          const cleaned = prev.filter((m) => !m.isDeptGreeting);
          return [...cleaned, { role: 'assistant', text: DEPARTMENT_GREETINGS[category], isDeptGreeting: true, category }];
        });
      }, 800);
      return () => clearTimeout(timer);
    }

    // Already introduced this session → do NOT auto-open the panel. Just quietly stash the greeting
    // in the message list (so it's there when the user opens Shannon) and show a subtle "still here" toast.
    setMessages((prev) => {
      const cleaned = prev.filter((m) => !m.isDeptGreeting);
      return [...cleaned, { role: 'assistant', text: DEPARTMENT_GREETINGS[category], isDeptGreeting: true, category }];
    });
    if (!userInteracted && !isOpen) {
      setShowStillHere(true);
      if (stillHereTimerRef.current) clearTimeout(stillHereTimerRef.current);
      stillHereTimerRef.current = setTimeout(() => setShowStillHere(false), STILL_HERE_TOAST_MS);
    }
  }, [location.search, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up any pending timers on unmount
  useEffect(() => () => {
    cancelAutoClose();
    if (stillHereTimerRef.current) clearTimeout(stillHereTimerRef.current);
  }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!input.trim() && !pendingImage) || loading) return;

    // User has now actively engaged — stop auto-close, stop still-here toasts
    setUserInteracted(true);
    cancelAutoClose();
    setShowStillHere(false);

    const userMsg = input.trim() || (pendingImage ? "Can you find this in your store?" : "");
    const imageForSend = pendingImage;
    setInput('');
    setPendingImage(null);
    setMessages((prev) => [...prev, {
      role: 'user',
      text: userMsg,
      imageUrl: imageForSend ? imageForSend.dataUrl : null,
    }]);
    setLoading(true);

    try {
      // Detect current page context (category param on /products, or the pathname for brand pages)
      const params = new URLSearchParams(location.search);
      const currentPage = params.get('category') || (location.pathname === '/footwear' ? 'footwear' : null);
      const payload = {
        message: userMsg,
        session_id: getSessionId(),
        current_page: currentPage,
      };
      if (imageForSend) payload.image_base64 = imageForSend.base64;
      const res = await axios.post(`${API}/chat`, payload);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: res.data.response,
        products: res.data.products || [],
      }]);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', text: "Oops! I'm having a moment. Try again or reach us at DoeWizapparel@gmail.com!" }]);
    } finally {
      setLoading(false);
    }
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          const s = Math.min(MAX / w, MAX / h);
          w = Math.round(w * s); h = Math.round(h * s);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
        const base64 = dataUrl.split(',')[1];
        setPendingImage({ dataUrl, base64 });
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const quickQuestions = [
    "What's on sale?",
    "Help me style an outfit",
    "Shipping info",
    "Size guide",
  ];

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            data-testid="shannon-fab"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-[80] group"
          >
            <div className={`relative w-16 h-16 rounded-full overflow-hidden border-3 border-[#0A0A0A] shadow-[3px_3px_0px_#0A0A0A] hover:shadow-[5px_5px_0px_#0A0A0A] transition-all hover:-translate-y-0.5 ${pulse ? 'animate-bounce' : ''}`}>
              <img src={SHANNON_AVATAR} alt="Shannon" className="w-full h-full object-cover" />
            </div>
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#22C55E] rounded-full border-2 border-white" />
            <div className="absolute bottom-full right-0 mb-2 bg-[#0A0A0A] text-white text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Chat with Shannon
            </div>
            {/* Subtle "still here" toast for returning shoppers on subsequent pages */}
            <AnimatePresence>
              {showStillHere && (
                <motion.div
                  data-testid="shannon-still-here-toast"
                  initial={{ opacity: 0, x: 20, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 20, scale: 0.9 }}
                  transition={{ duration: 0.25 }}
                  className="absolute right-[72px] bottom-1 w-56 bg-white border-2 border-[#0A0A0A] rounded-xl px-3 py-2 shadow-[3px_3px_0px_#0A0A0A] pointer-events-none"
                >
                  <div className="text-[11px] uppercase tracking-widest font-bold text-[#525252]">Shannon</div>
                  <div className="text-xs text-[#0A0A0A] leading-snug">Still here if you need me — tap anytime.</div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="shannon-chat-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-4 right-4 z-[90] w-[360px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-100px)] flex flex-col border-2 border-[#0A0A0A] shadow-[6px_6px_0px_#0A0A0A] bg-[#FDFBF7] overflow-hidden"
          >
            {/* Header */}
            <div className="bg-[#0A0A0A] px-4 py-3 flex items-center gap-3 shrink-0">
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#FAD4D4] shrink-0">
                <img src={SHANNON_AVATAR} alt="Shannon" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-bold text-sm flex items-center gap-1.5">
                  Shannon <Sparkles className="w-3.5 h-3.5 text-[#FAD4D4]" />
                </h3>
                <p className="text-[#999] text-xs">Personal Shopping Assistant</p>
              </div>
              <button
                data-testid="shannon-close"
                onClick={() => setIsOpen(false)}
                className="text-white/70 hover:text-white p-1 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="shannon-messages">
              {messages.map((msg, i) => (
                <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} w-full`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-full overflow-hidden border border-[#E5E5E5] shrink-0 mr-2 mt-0.5">
                        <img src={SHANNON_AVATAR} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div
                      className={`max-w-[75%] px-3 py-2 text-sm leading-relaxed whitespace-pre-line ${
                        msg.role === 'user'
                          ? 'bg-[#0A0A0A] text-white rounded-t-xl rounded-bl-xl'
                          : 'bg-[#F5F3EF] text-[#0A0A0A] border border-[#E5E5E5] rounded-t-xl rounded-br-xl'
                      }`}
                    >
                      {msg.imageUrl && (
                        <img
                          src={msg.imageUrl}
                          alt="uploaded"
                          className="mb-2 max-w-full max-h-40 rounded border border-white/20"
                          data-testid={`user-upload-${i}`}
                        />
                      )}
                      {msg.text}
                    </div>
                  </div>

                  {/* Attached product cards — Shannon's extra mile */}
                  {msg.role === 'assistant' && msg.products && msg.products.length > 0 && (
                    <div className="ml-9 mt-2 w-[calc(100%-2.25rem)] space-y-2" data-testid={`shannon-products-${i}`}>
                      {msg.products.map((p) => {
                        const discounted = p.is_promo && p.promo_percentage
                          ? p.price * (1 - p.promo_percentage / 100)
                          : null;
                        return (
                          <Link
                            key={p.id}
                            to={p.link || `/product/${p.id}`}
                            onClick={() => setIsOpen(false)}
                            data-testid={`shannon-product-${p.id}`}
                            className="group flex items-center gap-3 bg-white border-2 border-[#0A0A0A] p-2 hover:shadow-[3px_3px_0px_#0A0A0A] hover:-translate-y-0.5 transition-all"
                          >
                            <div className="w-14 h-14 bg-[#F5F5F5] overflow-hidden flex-shrink-0 border border-[#E5E5E5]">
                              {p.image && (
                                <img src={(p.image || '').includes('customer-assets.emergentagent.com') && !(p.image || '').endsWith('.webp') ? `https://images.weserv.nl/?url=${encodeURIComponent((p.image || '').replace(/^https?:\/\//i, ''))}&w=400&q=70&output=webp` : p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] uppercase tracking-wider text-[#999]">{p.category}</p>
                              <p className="text-xs font-semibold leading-tight line-clamp-2 group-hover:underline">{p.name}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {discounted ? (
                                  <>
                                    <span className="text-xs font-bold text-[#D32027]">{formatPrice(discounted)}</span>
                                    <span className="text-[10px] text-[#999] line-through">{formatPrice(p.price)}</span>
                                  </>
                                ) : (
                                  <span className="text-xs font-bold">{formatPrice(p.price)}</span>
                                )}
                                {p.is_promo && p.promo_percentage && (
                                  <span className="text-[9px] font-bold bg-[#FAD4D4] text-[#0A0A0A] px-1">-{p.promo_percentage}%</span>
                                )}
                              </div>
                            </div>
                            <ExternalLink className="w-4 h-4 text-[#525252] group-hover:text-[#0A0A0A] flex-shrink-0" />
                          </Link>
                        );
                      })}
                      <p className="text-[10px] text-[#999] italic pl-1">Tap any item to view & buy</p>
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="w-7 h-7 rounded-full overflow-hidden border border-[#E5E5E5] shrink-0 mr-2 mt-0.5">
                    <img src={SHANNON_AVATAR} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="bg-[#F5F3EF] border border-[#E5E5E5] rounded-t-xl rounded-br-xl px-4 py-3">
                    <div className="flex gap-1.5">
                      <span className="w-2 h-2 bg-[#999] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-[#999] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-[#999] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Quick questions on first load */}
              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {quickQuestions.map((q) => (
                    <button
                      key={q}
                      data-testid={`quick-q-${q.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      onClick={() => { setInput(q); }}
                      className="text-xs bg-white border border-[#D4E2FA] text-[#0A0A0A] px-3 py-1.5 rounded-full hover:bg-[#D4E2FA] transition-colors font-medium"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="border-t-2 border-[#0A0A0A] bg-white shrink-0">
              {/* Pending image preview */}
              {pendingImage && (
                <div className="px-3 pt-2 flex items-center gap-2" data-testid="shannon-pending-image">
                  <div className="relative">
                    <img src={pendingImage.dataUrl} alt="preview" className="w-14 h-14 object-cover border-2 border-[#0A0A0A]" />
                    <button
                      type="button"
                      onClick={() => setPendingImage(null)}
                      data-testid="shannon-remove-image"
                      className="absolute -top-1.5 -right-1.5 bg-[#0A0A0A] text-white rounded-full w-5 h-5 flex items-center justify-center"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-[11px] text-[#525252]">I'll check our catalog for this and find matches.</p>
                </div>
              )}
              <div className="px-3 py-3 flex gap-2 items-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImagePick}
                  className="hidden"
                  data-testid="shannon-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="shannon-image-btn"
                  disabled={loading}
                  title="Upload a photo to find it in our store"
                  className="p-2 border-2 border-[#E5E5E5] hover:border-[#0A0A0A] hover:bg-[#FAD4D4] transition-colors shrink-0 disabled:opacity-40"
                >
                  <Camera className="w-4 h-4" />
                </button>
                <input
                  data-testid="shannon-input"
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={pendingImage ? "Add a note (optional)..." : "Ask Shannon anything..."}
                  className="flex-1 text-sm px-3 py-2 border-2 border-[#E5E5E5] focus:border-[#0A0A0A] outline-none transition-colors bg-[#FDFBF7]"
                  disabled={loading}
                />
                <button
                  data-testid="shannon-send"
                  type="submit"
                  disabled={loading || (!input.trim() && !pendingImage)}
                  className="bg-[#0A0A0A] text-white p-2 hover:bg-[#0A0A0A]/80 disabled:opacity-40 transition-colors shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
